import React, { useState, useEffect } from 'react';
import {
  BarChart3, FileJson, Printer, ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { reportsService } from '../services';
import { formatDate } from '../lib/utils';
import { AppLayout } from '../components/AppLayout';
import { toast } from 'sonner';

import { EMPTY_STATES } from '../design-system/content';
import { getScoreBandClass, getScoreBandColor } from '../design-system/tokens';
import { toReportVM } from '../design-system/viewmodels';
import { ScoreDisplay, MetricCard, PageHeader, EmptyState, VerdictBadge } from '../components/shared';

function PrintableReport({ report }) {
  if (!report) return null;

  return (
    <div className="space-y-6 text-sm print:text-black" id="printable-report" data-testid="printable-report">
      {/* Header */}
      <div className="border-b border-white/10 pb-4 print:border-black/20">
        <h2 className="text-lg font-bold text-white print:text-black">MSML HEALTH Analysis Report</h2>
        <p className="text-xs text-[var(--msml-text-muted)] print:text-gray-500">
          Run: {report.runId} | {formatDate(report.createdAt)}
        </p>
      </div>

      {/* Executive Summary */}
      <div>
        <h3 className="text-sm font-medium text-[var(--msml-text-secondary)] mb-3 print:text-gray-700">Executive Summary</h3>
        <div className="flex items-center gap-6 mb-3">
          <div>
            <p className="text-[10px] text-[var(--msml-text-muted)] uppercase">HQ Score</p>
            <p className={`text-3xl font-bold ${getScoreBandClass(report.hqScore)}`}>{report.hqScore?.toFixed(1)}</p>
            <p className="text-xs text-[var(--msml-text-muted)]">{report.scoreBand}</p>
          </div>
          <div>
            <p className="text-[10px] text-[var(--msml-text-muted)] uppercase">Raw Score</p>
            <p className="text-lg text-[var(--msml-text-secondary)]">{report.hqScoreRaw?.toFixed(1)}</p>
          </div>
        </div>
        {report.capsApplied?.length > 0 && (
          <div className="text-xs text-[var(--msml-warning)] mb-2">Caps: {report.capsApplied.join('; ')}</div>
        )}
      </div>

      {/* Dimensions */}
      <div>
        <h3 className="text-sm font-medium text-[var(--msml-text-secondary)] mb-3 print:text-gray-700">Dimension Scores</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {report.dimensions.map(d => (
            <div key={d.key} className="msml-panel p-3 print:border print:border-gray-300">
              <p className="text-[10px] text-[var(--msml-text-muted)] uppercase tracking-wider">{d.label}</p>
              <p className={`text-lg font-bold ${getScoreBandClass(d.score)}`}>{d.score?.toFixed(1)}</p>
              {d.drivers?.length > 0 && (
                <p className="text-[10px] text-[var(--msml-text-muted)] line-clamp-1 mt-0.5">{d.drivers[0].reason}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Input text */}
      <div>
        <h3 className="text-sm font-medium text-[var(--msml-text-secondary)] mb-2 print:text-gray-700">Original Text</h3>
        <div className="msml-panel p-3 print:border print:border-gray-300">
          <p className="text-xs text-[var(--msml-text-secondary)] print:text-black whitespace-pre-wrap">{report.input?.original_text}</p>
          <div className="flex gap-3 mt-2 text-[10px] text-[var(--msml-text-muted)]">
            {report.input?.domain && <span>Domain: {report.input.domain}</span>}
            {report.input?.audience && <span>Audience: {report.input.audience}</span>}
          </div>
        </div>
      </div>

      {/* Optimization */}
      {report.optimization?.optimized_text && (
        <div>
          <h3 className="text-sm font-medium text-[var(--msml-success)] mb-2">Optimized Rewrite</h3>
          <div className="msml-panel p-3 border-l-2 border-[var(--msml-success)] print:border print:border-green-500">
            <p className="text-xs text-[var(--msml-text-secondary)] print:text-black whitespace-pre-wrap">{report.optimization.optimized_text}</p>
            {report.optimization.improvement_summary && (
              <p className="text-[10px] text-[var(--msml-text-muted)] mt-2">{report.optimization.improvement_summary}</p>
            )}
          </div>
        </div>
      )}

      {/* Judge */}
      {report.judge && (
        <div>
          <h3 className="text-sm font-medium text-[var(--msml-text-secondary)] mb-2 print:text-gray-700">Judge Result</h3>
          <div className="msml-panel p-3 print:border print:border-gray-300">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-[var(--msml-text-muted)]">Intent: </span>
                <span className={report.judge.intent_status === 'pass' ? 'text-[var(--msml-success)]' : report.judge.intent_status === 'fail' ? 'text-[var(--msml-danger)]' : 'text-[var(--msml-warning)]'}>
                  {report.judge.intent_status} ({report.judge.intent_similarity?.toFixed(2)})
                </span>
              </div>
              <div>
                <span className="text-[var(--msml-text-muted)]">Safety: </span>
                <span className={report.judge.safety_status === 'pass' ? 'text-[var(--msml-success)]' : 'text-[var(--msml-warning)]'}>{report.judge.safety_status}</span>
              </div>
              <div>
                <span className="text-[var(--msml-text-muted)]">Verdict: </span>
                <VerdictBadge verdict={report.judge.overall_verdict} />
              </div>
            </div>
            {report.judge.notes && <p className="text-[10px] text-[var(--msml-text-muted)] mt-2">{report.judge.notes}</p>}
          </div>
        </div>
      )}

      {/* Trace Metadata */}
      <div>
        <h3 className="text-sm font-medium text-[var(--msml-text-secondary)] mb-2 print:text-gray-700">Trace Metadata</h3>
        <div className="msml-panel p-3 print:border print:border-gray-300">
          <div className="grid grid-cols-2 gap-1.5 text-[10px] text-[var(--msml-text-muted)]">
            <span>Rubric: {report.trace?.rubric_version}</span>
            <span>Prompt: {report.trace?.prompt_version}</span>
            <span>Scorer: {report.trace?.scoring_model_id}</span>
            <span>Optimizer: {report.trace?.optimizer_model_id}</span>
            <span>Judge: {report.trace?.judge_model_id}</span>
            <span>Mode: {report.trace?.processing_mode}</span>
            <span>Status: {report.trace?.status}</span>
            <span>{formatDate(report.trace?.created_at)}</span>
            <span className="col-span-2 font-mono break-all">Hash: {report.trace?.determinism_hash}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [distribution, setDistribution] = useState([]);
  const [optimizedDist, setOptimizedDist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => { loadReports(); }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const data = await reportsService.get();
      setStats(data.stats || {});
      setRecent(data.recent_analyses || []);
      setDistribution(data.score_distribution || []);
      setOptimizedDist(data.optimized_distribution || []);
    } catch (err) {
      console.error('Failed to load reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const openReport = async (runId) => {
    setSelectedReport(runId);
    setReportLoading(true);
    try {
      const data = await reportsService.getRunReport(runId);
      setReportData(toReportVM(data));
    } catch (err) {
      toast.error('Failed to load report');
    } finally {
      setReportLoading(false);
    }
  };

  const handleJsonExport = async (runId) => {
    try {
      const data = await reportsService.getRunJsonExport(runId);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `msml-report-${runId}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('JSON exported');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handlePrint = () => window.print();

  return (
    <AppLayout activeNav="reports">
      <div className="p-4 lg:p-6">
        <div className="max-w-4xl mx-auto">
          <PageHeader
            icon={<BarChart3 className="w-5 h-5 text-[var(--msml-success)]" />}
            iconBg="bg-[var(--msml-success-bg)]"
            title="Reports"
            subtitle="Executive summaries, technical traces, and exports"
          />

          {loading ? (
            <div className="flex justify-center py-16"><span className="msml-spinner" /></div>
          ) : (
            <>
              {/* Stats cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                <MetricCard label="Total Analyses" value={stats?.total_analyses || 0} />
                <MetricCard label="Average HQ" value={(stats?.avg_score || 0).toFixed(1)} color={getScoreBandClass(stats?.avg_score)} />
                <MetricCard label="Best Score" value={(stats?.max_score || 0).toFixed(1)} color="text-[var(--msml-success)]" />
                <MetricCard label="Lowest Score" value={(stats?.min_score || 0).toFixed(1)} color="text-[var(--msml-warning)]" />
              </div>

              {/* Score distribution */}
              {distribution.length > 0 && (
                <div className="msml-card mb-6" data-testid="score-distribution">
                  <h3 className="text-sm font-medium text-white mb-4">Score Distribution</h3>
                  <div className="flex items-center gap-4 mb-4 text-[10px]">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: 'rgba(139,92,246,0.7)' }} />
                      <span className="text-[var(--msml-text-muted)]">Original Input</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: 'rgba(34,197,94,0.7)' }} />
                      <span className="text-[var(--msml-text-muted)]">After Optimization</span>
                    </span>
                  </div>
                  <div className="space-y-3">
                    {distribution.map((b, i) => {
                      const optBucket = optimizedDist[i] || { count: 0 };
                      const maxVal = Math.max(
                        ...distribution.map(x => x.count),
                        ...optimizedDist.map(x => x.count),
                        1
                      );
                      const origPct = (b.count / maxVal) * 100;
                      const optPct = (optBucket.count / maxVal) * 100;
                      const bandColors = {
                        'Poor': '#ef4444',
                        'Acceptable': '#f59e0b',
                        'Good': '#3b82f6',
                        'Very Good': '#8b5cf6',
                        'Excellent': '#22c55e',
                      };
                      return (
                        <div key={b.range} data-testid={`dist-band-${b.label.toLowerCase().replace(' ','-')}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium w-20" style={{ color: bandColors[b.label] || '#8b5cf6' }}>
                              {b.label}
                            </span>
                            <span className="text-[10px] text-[var(--msml-text-muted)]">{b.range}</span>
                          </div>
                          {/* Original bar */}
                          <div className="flex items-center gap-2 mb-1">
                            <div className="flex-1 h-5 rounded overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)' }}>
                              <div
                                className="h-full rounded transition-all duration-500"
                                style={{
                                  width: `${Math.max(origPct, b.count > 0 ? 2 : 0)}%`,
                                  background: 'rgba(139,92,246,0.7)',
                                }}
                              />
                            </div>
                            <span className="text-xs text-[var(--msml-text-secondary)] w-8 text-right tabular-nums">{b.count}</span>
                          </div>
                          {/* Optimized bar */}
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-5 rounded overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)' }}>
                              <div
                                className="h-full rounded transition-all duration-500"
                                style={{
                                  width: `${Math.max(optPct, optBucket.count > 0 ? 2 : 0)}%`,
                                  background: 'rgba(34,197,94,0.7)',
                                }}
                              />
                            </div>
                            <span className="text-xs text-[var(--msml-text-secondary)] w-8 text-right tabular-nums">{optBucket.count}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Recent analyses */}
              <div className="msml-card" data-testid="recent-analyses">
                <h3 className="text-sm font-medium text-white mb-3">Recent Analyses</h3>
                {recent.length === 0 ? (
                  <p className="text-xs text-[var(--msml-text-muted)] py-8 text-center">No analyses yet</p>
                ) : (
                  <div className="space-y-2">
                    {recent.map(item => (
                      <div
                        key={item.analysis_id}
                        className="flex items-center justify-between p-3 rounded-lg bg-[var(--msml-bg)] hover:bg-[var(--msml-bg)]/80 cursor-pointer transition-colors"
                        onClick={() => openReport(item.analysis_id)}
                        data-testid={`report-${item.analysis_id}`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <ScoreDisplay score={item.hq_score} size="sm" />
                          <div className="min-w-0">
                            <p className="text-xs text-[var(--msml-text-secondary)] truncate">{item.original_text?.substring(0, 80)}</p>
                            <p className="text-[10px] text-[var(--msml-text-muted)]">{formatDate(item.created_at)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2 shrink-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleJsonExport(item.analysis_id); }}
                            className="p-1.5 rounded-md hover:bg-white/5 text-[var(--msml-text-muted)] hover:text-[var(--msml-primary)]"
                            data-testid={`export-${item.analysis_id}`}
                          >
                            <FileJson className="w-3.5 h-3.5" />
                          </button>
                          <ChevronRight className="w-4 h-4 text-[var(--msml-text-muted)]" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Report detail modal */}
          <Dialog open={!!selectedReport} onOpenChange={() => { setSelectedReport(null); setReportData(null); }}>
            <DialogContent className="bg-[var(--msml-panel)] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="text-white">Analysis Report</DialogTitle>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => handleJsonExport(selectedReport)}
                      className="text-xs text-[var(--msml-text-muted)] hover:text-white" data-testid="export-json-btn"
                    >
                      <FileJson className="w-3 h-3 mr-1" /> JSON
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handlePrint}
                      className="text-xs text-[var(--msml-text-muted)] hover:text-white" data-testid="print-report-btn"
                    >
                      <Printer className="w-3 h-3 mr-1" /> Print
                    </Button>
                  </div>
                </div>
              </DialogHeader>
              {reportLoading ? (
                <div className="flex justify-center py-12"><span className="msml-spinner" /></div>
              ) : (
                <PrintableReport report={reportData} />
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </AppLayout>
  );
}
